import {
	GetPlaceWizardBasicInformationStepResponse,
	GetPlaceWizardRolesAndEmployeesStepResponse,
	PlaceWizardRolesAndEmployeesBar,
	WizardBar,
	WizardRole,
} from "#resources/api/enterprise-generated";
import { LocalWizardEmployee } from "#pages/place/wizard/steps/employees/employees-mocks";
import { useQuery } from "@tanstack/react-query";
import { createQueryFactory } from "../query-factory";

export const { useApiHook: useGetPlaceWizardStatus } = createQueryFactory(
	"getPlaceWizardStatus",
	{
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	},
);

export const { useApiHook: useGetPlaceWizardBasicInformationStep } = createQueryFactory(
	"getPlaceWizardBasicInformationStep",
	{
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	},
);

export const useGetPlaceWizardConfiguration = (
	params: { placeId: string },
	options?: { enabled?: boolean },
) => {
	return useGetPlaceWizardBasicInformationStep(params, {
		enabled: options?.enabled !== false && Boolean(params.placeId),
	});
};

export const { useApiHook: useGetPlaceWizardBarsAndSectorsStep } = createQueryFactory(
	"getPlaceWizardBarsAndSectorsStep",
	{
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	},
);

export const { useApiHook: useGetPlaceWizardProductsAndMenusStep } = createQueryFactory(
	"getPlaceWizardProductsAndMenusStep",
	{
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	},
);

export const {
	useApiHook: useGetPlaceWizardRolesAndEmployeesStep,
} = createQueryFactory("getPlaceWizardRolesAndEmployeesStep", {
	retry: false,
	refetchOnWindowFocus: false,
	refetchOnMount: false,
	refetchOnReconnect: false,
});

// organizationRoles usa WizardRole (sem quantity) pois quantity é info de frontend,
// inferida no wrapper contando employees por cargo.
export interface WizardEmployeesSummaryData {
	organizationRoles: WizardRole[];
	employees: LocalWizardEmployee[];
	bars: WizardBar[];
}

const DEFAULT_EMPLOYEES_SUMMARY_MOCK: WizardEmployeesSummaryData = {
	organizationRoles: [
		{
			id: "sum-role-1",
			name: "Garçom",
			permissions: [
				{
					name: "Visualizar pedidos",
					slug: "view-orders",
					type: "order",
					isManagerial: false,
					isPaymentMethod: false,
				},
				{
					name: "Criar pedidos",
					slug: "create-orders",
					type: "order",
					isManagerial: false,
					isPaymentMethod: false,
				},
			],
		},
		{
			id: "sum-role-2",
			name: "Caixa",
			permissions: [
				{
					name: "Visualizar pedidos",
					slug: "view-orders",
					type: "order",
					isManagerial: false,
					isPaymentMethod: false,
				},
				{
					name: "Visualizar relatórios",
					slug: "view-reports",
					type: "report",
					isManagerial: true,
					isPaymentMethod: false,
				},
			],
		},
	],
	employees: [
		{
			id: "sum-emp-1",
			roleId: "sum-role-1",
			name: "Garçom 1",
			username: "summ.gar.01",
			password: "123",
			selectedPointsOfSale: ["sum-bar-1"],
		},
		{
			id: "sum-emp-2",
			roleId: "sum-role-1",
			name: "Garçom 2",
			username: "summ.gar.02",
			password: "123",
			selectedPointsOfSale: ["sum-bar-1"],
		},
		{
			id: "sum-emp-3",
			roleId: "sum-role-2",
			name: "Caixa 1",
			username: "summ.cai.01",
			password: "123",
			selectedPointsOfSale: [],
		},
	],
	bars: [{ id: "sum-bar-1", name: "Bar principal", productIds: [], menuIds: [] }],
};

// TODO: MOCK - Substituir por createQueryFactory("getPlaceWizardEmployeesSummaryData", ...)
// quando a API de resumo da etapa 4 estiver disponível no SDK.
export function useGetPlaceWizardEmployeesSummaryData(_params: { placeId: string }) {
	return useQuery<WizardEmployeesSummaryData>({
		queryKey: ["getPlaceWizardEmployeesSummaryData", _params],
		queryFn: () => Promise.resolve(DEFAULT_EMPLOYEES_SUMMARY_MOCK),
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});
}

export type {
	GetPlaceWizardBasicInformationStepResponse,
	GetPlaceWizardRolesAndEmployeesStepResponse,
	PlaceWizardRolesAndEmployeesBar,
	WizardRole,
};
